import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { Seo } from "../components/Seo";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

export const FAQS = [
  {
    q: "Is PhudG oil truly cold-pressed?",
    a: "Yes. Our oil is mechanically extracted from seeds using a traditional wooden ghani — not produced through solvent extraction or industrial refining. The temperature during extraction stays below 45°C.",
  },
  {
    q: "What is wooden ghani extraction?",
    a: "A ghani is a slow-turning wooden press — a mortar and pestle that crushes seeds without generating heat. Because it turns slowly, the oil keeps the seed's natural aroma, flavour and character. 'Kachi ghani' means the oil came out raw and cold.",
  },
  {
    q: "Does cold-pressed oil have a strong smell?",
    a: "Yes — and that's a good sign. The natural aroma of the seed survives because the oil is never heated or deodorised. Mustard oil is the most pungent of our four; heating it till it just smokes softens the sharpness before cooking.",
  },
  {
    q: "Can I use mustard oil for frying?",
    a: "Yes. Mustard oil is widely used for frying across North and East Indian cooking — from fish to pooris. Heat it till it just smokes, then cook as usual.",
  },
  {
    q: "How should I store PhudG oil?",
    a: "Tightly closed, in a cool dark cupboard — away from sunlight and the stove. Pour directly or use a clean, dry spoon; water is oil's enemy in storage.",
  },
  {
    q: "Why is cold-pressed oil more expensive?",
    a: "Wooden ghani pressing extracts less oil per kilo of seed than industrial methods, and we press in small batches. You're paying for lower yield and slower work — not marketing.",
  },
  {
    q: "What is the shelf life?",
    a: "Cold-pressed oils generally keep for a shorter time than refined oils — refining is partly done to extend shelf life. Check the best-before date on your bottle; once opened, use within a few months and store cool and dark.",
  },
  {
    q: "Is the oil filtered?",
    a: "Yes — the oil settles naturally and is then gently filtered. No chemical treatment at any stage.",
  },
  {
    q: "Is it suitable for everyday cooking?",
    a: "Absolutely. Each oil suits different jobs: black mustard for tadka and pickles, yellow mustard for lighter everyday cooking, groundnut for frying, and sunflower when you want a neutral base.",
  },
];

export const FaqAccordion = ({ items, testidPrefix }) => {
  const [open, setOpen] = useState(0);
  return (
    <div className="divide-y divide-ink/10 border-y border-ink/10">
      {items.map((f, i) => (
        <div key={i}>
          <button
            data-testid={`${testidPrefix}-question-${i}`}
            onClick={() => setOpen(open === i ? -1 : i)}
            className="flex w-full items-center justify-between gap-6 py-6 text-left"
          >
            <span className="font-serif text-xl md:text-2xl text-ink">{f.q}</span>
            <ChevronDown
              size={20}
              className={`shrink-0 text-amberbrand transition-transform duration-300 ${open === i ? "rotate-180" : ""}`}
            />
          </button>
          <AnimatePresence initial={false}>
            {open === i && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                className="overflow-hidden"
              >
                <p data-testid={`${testidPrefix}-answer-${i}`} className="pb-6 max-w-2xl text-sm md:text-base font-light text-ink/60 leading-relaxed">
                  {f.a}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

export default function FaqPage() {
  return (
    <main data-testid="faq-page">
      <Seo title={"Cold-Pressed Oil FAQs | Mustard, Groundnut and Sunflower Oil | PhudG"} description={"Find answers to common questions about cold-pressed oils, wooden ghani extraction, cooking, storage, filtration and choosing the right oil."} path="/faq" />
      <Header />
      <section className="bg-ink grain relative pt-36 pb-16 overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-xs tracking-[0.4em] uppercase text-gold mb-4">FAQ</p>
          <h1 className="font-serif text-5xl md:text-7xl text-cream tracking-tighter">
            Questions, <span className="italic text-gold">answered.</span>
          </h1>
        </div>
      </section>
      <section className="bg-cream py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6">
          <FaqAccordion items={FAQS} testidPrefix="faq" />
        </div>
      </section>
      <Footer />
    </main>
  );
}
